import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { CartService } from 'src/shared/services/cart.service';
import { DataService } from 'src/shared/data/data.service';
// import { loadStripe } from '@stripe/stripe-js';

import {
  loadStripe,
  Stripe,
  StripeElements,
  StripePaymentElement
} from '@stripe/stripe-js';

@Component({
  selector: 'app-checkout-modal',
  templateUrl: './checkout-modal.component.html',
  styleUrl: './checkout-modal.component.scss'
})


export class CheckoutModalComponent implements OnInit, OnChanges {
  @Input() show: boolean = false;
  @Input() orderData: any;
  @Output() close = new EventEmitter<void>();

  form!: FormGroup;
  showPassword = false;
  customerExists = false;

  stripe: Stripe | null = null;
  elements!: StripeElements;
  paymentElement!: StripePaymentElement;
  clientSecret = '';
  cardMounted = false;

  // UI State Variables
  isProcessing = false;
  isLoadingPaymentElement = false;
  errorMessage: string | null = null;
  orderSuccess = false;
  placedOrderId = '';

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private cart: CartService,
    private data: DataService
  ) { }

  async ngOnInit() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.pattern(/^[a-zA-Z0-9 ]+$/)]],
      mobile: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]]
    });

    this.checkCustomer();

    try {
      this.stripe = await loadStripe(this.data.stripePublishableKey);
    } catch (err) {
      console.error('Stripe SDK load error:', err);
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['show']) {
      if (this.show) {
        this.resetModalState();
        this.checkCustomer();
        await this.initializeStripePayment();
      } else {
        this.cleanupStripePayment();
      }
    }
  }

  async initializeStripePayment() {
    if (!this.stripe) {
      try {
        this.stripe = await loadStripe(this.data.stripePublishableKey);
      } catch (err) {
        console.error('Stripe initialization failed:', err);
      }
    }

    if (!this.stripe) {
      this.errorMessage = 'Secure payment gateway is temporarily unavailable. Please try again later.';
      return;
    }

    if (!this.orderData || !this.orderData.total) {
      this.errorMessage = 'Invalid order details. Please review your cart.';
      return;
    }

    this.isLoadingPaymentElement = true;
    this.errorMessage = null;

    const paymentPayload = {
      amount: Math.round(this.orderData.total * 100) // Convert to cents for Stripe
    };

    this.http.post<any>(
      this.data.base_url + 'create_payment_intent/',
      paymentPayload
    ).subscribe({
      next: (res) => {
        if (!res || !res.clientSecret) {
          this.errorMessage = 'Could not secure your transaction. Please try again.';
          this.isLoadingPaymentElement = false;
          return;
        }

        this.clientSecret = res.clientSecret;

        // Elegant custom styling for Stripe elements using the appearance API
        const appearance = {
          theme: 'flat' as const,
          variables: {
            colorPrimary: '#ff6b35',
            colorBackground: '#ffffff',
            colorText: '#2d3748',
            colorDanger: '#e53e3e',
            fontFamily: 'Source Sans Pro, system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '8px',
          },
          rules: {
            '.Input': {
              border: '1px solid #e2e8f0',
              boxShadow: 'none',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              padding: '11px 12px',
              fontSize: '14px',
            },
            '.Input:focus': {
              border: '1px solid #ff6b35',
              boxShadow: '0 0 0 3px rgba(255, 107, 53, 0.12)',
            },
            '.Label': {
              fontWeight: '500',
              color: '#4a5568',
              fontSize: '13px',
              marginBottom: '6px',
            }
          }
        };

        this.elements = this.stripe!.elements({
          clientSecret: this.clientSecret,
          appearance
        });

        this.paymentElement = this.elements.create('payment');

        setTimeout(() => {
          const container = document.getElementById('payment-element');
          if (container) {
            this.paymentElement.mount('#payment-element');
            this.cardMounted = true;
          }
          this.isLoadingPaymentElement = false;
        }, 50);
      },
      error: (err) => {
        console.error('Payment intent generation error:', err);
        this.errorMessage = 'Failed to load payment gateway. Please close the checkout and try again.';
        this.isLoadingPaymentElement = false;
      }
    });
  }

  cleanupStripePayment() {
    if (this.paymentElement) {
      try {
        this.paymentElement.unmount();
        this.paymentElement.destroy();
      } catch (e) {
        console.error('Error during Stripe element cleanup:', e);
      }
    }
    this.cardMounted = false;
    this.clientSecret = '';
  }

  resetModalState() {
    this.isProcessing = false;
    this.isLoadingPaymentElement = false;
    this.errorMessage = null;
    this.orderSuccess = false;
    this.placedOrderId = '';
  }

  closeModal() {
    this.cleanupStripePayment();
    this.resetModalState();
    this.form.reset();
    this.close.emit();
  }

  checkCustomer() {
    const isLoggedIn = localStorage.getItem('is_logged_in');
    if (isLoggedIn === 'true') {
      this.customerExists = true;
      this.showPassword = false;

      const customerData = {
        email: localStorage.getItem('customer_email'),
        customer_id: localStorage.getItem('customer_id'),
        name: localStorage.getItem('customer_name'),
        mobile: localStorage.getItem('mobile_number'),
      };

      this.form.patchValue({
        email: customerData.email,
        customer_id: customerData.customer_id,
        name: customerData.name,
        mobile: customerData.mobile,
      });
    } else {
      this.customerExists = false;
      this.showPassword = true;
    }
  }

  restrictToNumbers(event: any): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
  }

  preventNonNumeric(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (allowedKeys.includes(event.key) || (event.ctrlKey && event.key === 'v')) {
      return;
    }
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  async placeOrder() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.stripe || !this.elements) {
      this.errorMessage = 'Secure payment section is not fully loaded. Please wait.';
      return;
    }

    this.isProcessing = true;
    this.errorMessage = null;

    try {
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        redirect: 'if_required',
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: this.form.value.name,
              email: this.form.value.email,
              phone: this.form.value.mobile
            }
          }
        }
      });

      if (error) {
        this.errorMessage = error.message || 'Payment confirmation failed.';
        this.isProcessing = false;
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        this.saveOrder(paymentIntent.id);
      } else {
        this.errorMessage = 'Your payment status is: ' + (paymentIntent?.status || 'Pending completion') + '. Please try again.';
        this.isProcessing = false;
      }
    } catch (err: any) {
      console.error('Payment confirmation caught exception:', err);
      this.errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      this.isProcessing = false;
    }
  }

  saveOrder(paymentIntentId: string) {
    const payload = {
      ...this.form.value,
      cart_items: this.orderData.items,
      subtotal: this.orderData.subtotal,
      tax: this.orderData.tax,
      total: this.orderData.total,
      payment_intent: paymentIntentId,
      payment_status: 'Paid',
      is_logged_in: localStorage.getItem('is_logged_in') === 'true'
    };

    this.http.post<any>(
      this.data.base_url + 'place_order/',
      payload
    ).subscribe({
      next: (res) => {
        this.isProcessing = false;
        if (res.status === 'success') {
          this.placedOrderId = res.order_id;
          this.orderSuccess = true;

          localStorage.setItem('order_placed', 'true');
          if (res.user_id) localStorage.setItem('customer_id', res.user_id);
          localStorage.setItem('customer_name', this.form.value.name);

          this.cart.clearCart();
          this.form.reset();
        } else {
          this.errorMessage = res.message || 'Failed to complete order registration on the backend. Please contact support.';
        }
      },
      error: (err) => {
        console.error('Order registration error:', err);
        this.isProcessing = false;
        this.errorMessage = 'Payment succeeded, but recording your order failed. Please contact support with Payment ID: ' + paymentIntentId;
      }
    });
  }
}
